import React, { useContext, useEffect, useRef, useState } from "react";
import { getHouseMainCarousel } from "./house-service";
import { Carousel, CarouselItems, CarouselMedia } from "./types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import { HouseCompetitionData } from "./contextProvider";
import { Autoplay, A11y, Navigation } from "swiper/modules";
import EndsBadge from "@/app/(custom-header)/prizes/house-v2/components/EndsBadge";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import LastChance from "@/app/(custom-header)/prizes/house-v2/components/LastChance";
import UKsNo1 from "@/app/(custom-header)/prizes/house-v2/components/UKsNo1";
import WinnersSlider from "@/app/(custom-header)/prizes/house-v2/components/WinnersSlider";
import EnterNowButton from "@/app/(custom-header)/prizes/house-v2/EnterNowButton";

const MainCarousel = () => {
  const isMobile = useIsMobile();
  const { setContextData } = useContext(HouseCompetitionData);

  const [carousel, setCarousel] = useState<Carousel[]>();
  const [swiperData, setSwiperData] = useState<CarouselMedia[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabName, setTabName] = useState<string>("Tour");
  const { prizeId } = useContext(HouseCompetitionData);
  useEffect(() => {
    if (prizeId) {
      getMainCarousel();
    }
  }, [prizeId]);
  const onTabClick = (item: Carousel, tabIndex: number) => {
    setSwiperData(item.Media);
    setActiveTabIndex(tabIndex);
    setActiveSlideIndex(0);
    setTabName(item.Name);
    if (swiperRef.current) {
      swiperRef.current?.slideTo(0); // Resets Swiper to first slide
      swiperRef.current?.autoplay.start();
      swiperRef.current?.update();
    }
  };

  const getMainCarousel = async () => {
    try {
      const response = await getHouseMainCarousel(prizeId, 'v2');
      const { Items } = (await response.json()) as CarouselItems;
      const photosTab = Items.find(item => item.Name === "Photos");
      setCarousel(Items);
      setSwiperData(Items[0].Media);
      setTabName(Items[0].Name);
      setContextData(prevState => ({
        ...prevState,
        homeSpecsImage: photosTab?.Media?.[3]?.Url ?? photosTab?.Media?.[0]?.Url ?? "",
      }));
    } catch (err) {
      console.log(`Error while fetching the carousel data - ${JSON.stringify(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const slides = swiperData.filter((item, index) => {
    if (index === 0 && tabName === "Photos" && !item.IsVideo) return false;
    return true;
  });

  return isLoading ? (
    <div className={"w-full min-h-[360px] aspect-[1/1] bg-[#f1f1f1] lg:h-[calc(100vh-60px)]"} />
  ) : (
    <div className={`size-full relative carousel-container-within-slide-navigation`}>
      <EndsBadge className={"top-6 right-6"} />
      <Swiper
        modules={[Autoplay, Navigation, A11y]}
        autoplay={
          !isMobile
            ? {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        slidesPerView={1}
        loop={false}
        spaceBetween={0}
        navigation={true}
        onSlideChangeTransitionEnd={swiper => {
          const currentSlide = slides[swiper.realIndex];

          setActiveSlideIndex(swiper.realIndex);

          if (currentSlide?.IsVideo) {
            swiper.autoplay.stop();
          } else {
            swiper.autoplay.start();
          }
        }}
        onSwiper={swiper => {
          swiperRef.current = swiper;

          // autoplay corect la load inițial
          if (slides[0]?.IsVideo) {
            swiper.autoplay.stop();
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          zIndex: 0,
        }}>
        {slides.map((mediaItem, index) => {
          return (
            <SwiperSlide key={index} style={{ height: "100%", width: "100%" }}>
              <div className="size-full aspect-[1/1] lg:aspect-[1920/1020] flex overflow-hidden relative">
                {!mediaItem.IsVideo && !isMobile && (
                  <div
                    className={`absolute size-full inset-0 bg-gradient-to-t to-transparent ${
                      index === 0 && tabName === "Photos" ? "from-black/60" : "from-black/40"
                    }`}
                  />
                )}

                {!mediaItem.IsVideo ? (
                  <img
                    src={mediaItem.Url}
                    alt={`slide-${index}`}
                    className="size-full object-cover object-top"
                    loading="lazy"
                  />
                ) : (
                  <video src={mediaItem.Url} autoPlay loop muted playsInline className="size-full object-cover" />
                )}

                {/*{slides.length > 1 && (*/}
                {/*  <span className="absolute bottom-5 left-10 bg-[#00000080] rounded text-white px-1">*/}
                {/*    {activeSlideIndex + 1}/{slides.length}*/}
                {/*  </span>*/}
                {/*)}*/}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div
        className={
          "absolute bottom-0 lg:bottom-16 inset-x-0 mx-auto max-w-[1223px] flex flex-col items-center lg:gap-6 2xl:gap-[20px] 2xl:bottom-[24px] min-[1800px]:gap-[70px] min-[1800px]:bottom-[64px]"
        }>
        {!isMobile && (tabName === "Tour" || tabName === "Photos") && (
          <div className={"w-full flex flex-col items-center gap-10 2xl:gap-8 min-[1800px]:gap-10"}>
            <div className={"flex flex-col items-center justify-center gap-4"}>
              {/* new */}
              <LastChance />
              {/* new */}
              <UKsNo1 />
              {/* new */}
            </div>
            <div className={""}>
              <EnterNowButton text={"ENTER NOW »"} variation={"banner_desktop_btn"} place={"house_new_main_cta"} />
            </div>
            <div className={"w-[390px]"}>
              <WinnersSlider />
              {/* new */}
            </div>
          </div>
        )}
        <div className="w-full lg:w-auto lg:min-w-[800px] flex justify-around lg:justify-center items-center bg-[#F1F1F1] whitespace-nowrap lg:rounded-2xl">
          {carousel?.map((item, index) => (
            <div
              key={index}
              className={`flex-1 text-center font-jost text-[16px] font-normal p-3 cursor-pointer ${activeTabIndex === index ? "border-t-2 border-solid border-t-[#FF8200] text-primary" : "text-[#696971]"} ${index === 0 ? "lg:rounded-l-2xl" : ""} ${index === carousel.length - 1 ? "lg:rounded-r-2xl" : ""}`}
              onClick={() => onTabClick(item, index)}>
              {item.Name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MainCarousel;
